import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { isTerminalRunStatus, useRunProgress } from '@/lib/invoice-run-hooks';
import { queryKeys } from '@/lib/query-keys';

/**
 * Watches one run and reports it through a toast.
 *
 * This is the WHOLE in-flight surface. Closing the tab loses nothing: the run
 * continues server-side, the sweeper finishes it if the worker dies, and
 * anything unresolved shows up in the Not-invoiced panel next time. So this
 * deliberately does not persist, block, or own a page.
 *
 * Renders nothing — it is a behaviour, mounted while a run id is held.
 */
export function InvoiceRunProgress({
  runId,
  onFinished,
}: {
  runId: string;
  onFinished: () => void;
}) {
  const { data } = useRunProgress(runId);
  const queryClient = useQueryClient();
  const toastId = useRef<string | number | undefined>(undefined);
  // Guards the completion branch. The poll settles on a terminal status and
  // then keeps returning it from cache, so without this the finished toast is
  // re-raised on every re-render.
  const settled = useRef(false);

  const progress = data?.data;

  useEffect(() => {
    if (!progress || settled.current) return;

    if (!isTerminalRunStatus(progress.status)) {
      toastId.current = toast.loading(
        `Generating invoices… ${progress.done} of ${progress.total}`,
        { id: toastId.current, duration: Number.POSITIVE_INFINITY },
      );
      return;
    }

    settled.current = true;

    // The list, the unbilled panel and the run pages all describe state this
    // run just changed. Invalidating the whole `invoices` prefix is blunt and
    // correct: a finished run can have touched any of them.
    queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });

    const created = `${progress.created} ${progress.created === 1 ? 'invoice' : 'invoices'} created`;
    if (progress.failed > 0) {
      toast.warning(`${created} · ${progress.failed} couldn’t be invoiced`, {
        id: toastId.current,
        duration: 10_000,
        description: 'See “not invoiced” below the invoice list.',
      });
    } else {
      toast.success(created, { id: toastId.current, duration: 6_000 });
    }
    onFinished();
  }, [progress, queryClient, onFinished]);

  // Dismiss an unsettled toast on unmount.
  //
  // The in-progress toast has an INFINITE duration — it is meant to be replaced
  // by the completion one, not to expire. But `<Toaster />` lives at app root
  // while this component lives inside the invoices page, so navigating away
  // mid-run would otherwise strand it: pinned forever, frozen at whatever count
  // it last saw, for a run that may have finished successfully minutes ago.
  //
  // Only when unsettled. A completion toast is a real message with a real
  // duration and must be allowed to sit there and be read.
  useEffect(() => {
    return () => {
      if (!settled.current && toastId.current !== undefined) toast.dismiss(toastId.current);
    };
  }, []);

  return null;
}
